import { Trailer, ITrailer } from "./Trailer";
import { TrailerAttachment } from "./TrailerAttachment";
import constants from "../constants";
import { getServerDate } from "../utils/storage/date";
import { getCurrentVehicle } from "../utils/device";
import { extend, hashToArray } from "../utils/vanilla";
import { getKeys } from "../utils/helpers";
import Api from "../services/api";
import { DriveStore } from "../store/reducers";
import type { ITrailerAttachment } from "hos-tools";

export class TrailerAttachments {
    private trailerAttachments: IIdDictionary<TrailerAttachment>;
    private readonly api: Api;
    private readonly store: DriveStore;

    constructor(api: Api, store: DriveStore) {
        this.trailerAttachments = {};
        this.api = api;
        this.store = store;
        this.fetch();
    }

    get length(): number {
        return this.models.length;
    }

    get models(): TrailerAttachment[] {
        return this.getCurrent();
    }

    add(attachments: TrailerAttachment[]) {
        for (let attachment of attachments) {
            this.trailerAttachments[attachment.id] = new TrailerAttachment(attachment);
        }
    }

    remove(id: string) {
        delete this.trailerAttachments[id];
    }

    get(id: string): TrailerAttachment {
        return this.trailerAttachments[id];
    }

    getCurrent(): TrailerAttachment[] {
        let now = getServerDate();
        let currentVehicle = getCurrentVehicle();
        let deviceId = currentVehicle ? currentVehicle.id : undefined;
        return this.search(now, now, deviceId);
    }

    fetch() {
        let user = this.api.getUser?.();
        if (!user) {
            return;
        }
        let storedAttachments = this.store.getState().trailerAttachments[user.id];
        let temp: IIdDictionary<TrailerAttachment> = {};
        if (storedAttachments) {
            for (let id of getKeys(storedAttachments)) {
                let entity = extend(true, {}, storedAttachments[id]) as ITrailerAttachment;
                temp[id] = new TrailerAttachment(entity);
            }
        }
        this.trailerAttachments = temp;
    }

    populate() {
        for (let key of getKeys(this.trailerAttachments)) {
            this.trailerAttachments[key].trailer = this.trailerAttachments[key].getAttachmentTrailer(this.store);
        }
    }

    search(fromDate?: Date, toDate?: Date, deviceId?: string): TrailerAttachment[] {
        let activeFromDate = (fromDate || constants.MIN_DATE).getTime();
        let activeToDate = (toDate || constants.MAX_DATE).getTime();
        let collection: TrailerAttachment[] = [];

        for (let id of getKeys(this.trailerAttachments)) {
            let attachment = this.trailerAttachments[id];

            if (!attachment.activeFrom || !attachment.activeTo) {
                continue;
            }

            let activeTo = new Date(attachment.activeTo).getTime();
            let activeFrom = new Date(attachment.activeFrom).getTime();

            if (activeTo >= activeFromDate && activeFrom <= activeToDate && (attachment._isTemp || !deviceId || (attachment.device).id === deviceId)) {
                collection.push(attachment);
            }
        }

        return collection;
    }

    searchForTrailers(fromDate?: Date, toDate?: Date, deviceId?: string) {
        return this.getTrailers(this.search(fromDate, toDate, deviceId));
    }

    getCurrentTrailers (): Trailer[] {
        return this.getTrailers(this.getCurrent());
    }

    findTrailer (trailerId: string): Trailer | null {
        let trailers = this.getCurrentTrailers();
        for (let i = 0; i < trailers.length; i++) {
            let trailer = trailers[i];
            if (trailer.id === trailerId) {
                return trailer;
            }
        }
        return null;
    }

    isTrailerAttached(trailer: ITrailer): boolean {
        let now = getServerDate().toISOString();
        for (let id of getKeys(this.trailerAttachments)) {
            let attachment = this.trailerAttachments[id];
            let activeTo = attachment.activeTo;

            if (attachment.trailer?.id === trailer.id && (!activeTo || activeTo > now)) {
                return true;
            }
        }
        return false;
    }

    getTrailers(trailerAttachments: TrailerAttachment[]): Trailer[] {
        let trailers = trailerAttachments.map(attachment => attachment.getAttachmentTrailer(this.store));
        let trailerDict: IIdDictionary<Trailer> = {};

        trailers.forEach(trailer => {
            trailerDict[trailer.id] = trailer;
        });

        return hashToArray(trailerDict);
    }

    toJSON(): any {
        let keys = getKeys(this.trailerAttachments);
        let trailersJson = new Array(keys.length);
        for (let i = 0; i < keys.length; i++) {
            trailersJson[i] = this.trailerAttachments[keys[i]].toJSON();
        }
        return trailersJson;
    }
}